/**
 * MultilingualInput — Governed multilingual field editor for admin panels.
 *
 * Props:
 *   value:       { en: "...", tr: "...", ... } or string
 *   onChange:    (newValue: object) => void
 *   label:       string
 *   required:    boolean (EN is always required when true)
 *   multiline:   boolean (textarea vs input)
 *   placeholder: string
 *   policy:      "translatable_required" | "translatable_optional" | "fallback_to_en"
 *                | "display_fallback_only" | "non_translatable"
 *   readOnly:    boolean
 */
import React, { useState, useCallback, useMemo } from 'react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ChevronDown, ChevronUp, Globe, Copy, AlertCircle, Check } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: 'EN' },
  { code: 'tr', name: 'Turkce', flag: 'TR' },
  { code: 'de', name: 'Deutsch', flag: 'DE' },
  { code: 'es', name: 'Espanol', flag: 'ES' },
  { code: 'ar', name: 'العربية', flag: 'AR' },
  { code: 'ru', name: 'Русский', flag: 'RU' },
  { code: 'fr', name: 'Francais', flag: 'FR' },
];

const OTHER_LANGS = LANGUAGES.slice(1);

const POLICY_LABELS = {
  translatable_required: { label: 'Required', color: 'text-red-600 bg-red-50' },
  translatable_optional: { label: 'Optional', color: 'text-amber-600 bg-amber-50' },
  fallback_to_en: { label: 'Fallback to EN', color: 'text-blue-600 bg-blue-50' },
  display_fallback_only: { label: 'Display fallback', color: 'text-gray-500 bg-gray-50' },
  non_translatable: { label: 'Not translatable', color: 'text-gray-400 bg-gray-50' },
};

const normalize = (val) => {
  if (!val) return { en: '', tr: '', de: '', es: '', ar: '', ru: '', fr: '' };
  if (typeof val === 'string') return { en: val, tr: '', de: '', es: '', ar: '', ru: '', fr: '' };
  const result = {};
  for (const l of LANGUAGES) {
    result[l.code] = (val[l.code] && typeof val[l.code] === 'string') ? val[l.code] : '';
  }
  return result;
};

export const MultilingualInput = ({
  value,
  onChange,
  label = '',
  required = false,
  multiline = false,
  placeholder = '',
  policy = 'translatable_required',
  readOnly = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const norm = normalize(value);

  // REVIEW FIX (react-hooks/rules-of-hooks): these three hooks used to sit
  // BELOW the `non_translatable` early return, so a field switching policy
  // changed the number of hooks React saw between renders. They are declared
  // unconditionally now; the rendered output is unchanged.
  const handleChange = useCallback((langCode, text) => {
    onChange({ ...norm, [langCode]: text });
  }, [norm, onChange]);

  const handleCopyFromEnglish = useCallback(() => {
    const enVal = norm.en;
    if (!enVal.trim()) return;
    const updated = { ...norm };
    for (const l of OTHER_LANGS) {
      if (!updated[l.code] || !updated[l.code].trim()) {
        updated[l.code] = enVal;
      }
    }
    onChange(updated);
  }, [norm, onChange]);

  // Completeness calculation
  const completeness = useMemo(() => {
    const filled = OTHER_LANGS.filter(l => norm[l.code]?.trim()).length;
    const total = OTHER_LANGS.length;
    const hasEn = !!norm.en?.trim();
    return { filled, total, hasEn };
  }, [norm]);

  // Non-translatable fields: render as single EN input
  if (policy === 'non_translatable') {
    const InputEl = multiline ? Textarea : Input;
    return (
      <div className="space-y-1" data-testid={`ml-input-${label.toLowerCase().replace(/\s+/g, '-')}`}>
        {label && (
          <Label className="text-xs font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
            <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded text-gray-400 bg-gray-50">Not translatable</span>
          </Label>
        )}
        <InputEl
          value={norm.en}
          onChange={(e) => onChange({ ...norm, en: e.target.value })}
          placeholder={placeholder}
          className="text-sm"
          required={required}
          readOnly={readOnly}
          data-testid="ml-input-en"
        />
      </div>
    );
  }

  const InputEl = multiline ? Textarea : Input;

  const policyInfo = POLICY_LABELS[policy] || POLICY_LABELS.translatable_required;

  // Status color for completeness badge
  const completenessColor = completeness.filled === completeness.total
    ? 'text-green-600 bg-green-50'
    : completeness.filled > 0
      ? 'text-amber-600 bg-amber-50'
      : 'text-gray-400 bg-gray-100';

  return (
    <div className="space-y-2" data-testid={`ml-input-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      {label && (
        <div className="flex items-center gap-2">
          <Label className="text-xs font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
          </Label>
          <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${policyInfo.color}`}>
            {policyInfo.label}
          </span>
        </div>
      )}

      {/* English — always visible, required */}
      <div className="flex items-start gap-2">
        <span className="inline-flex items-center justify-center h-8 px-2 bg-blue-100 text-blue-700 text-xs font-bold rounded shrink-0 mt-0.5">
          EN
        </span>
        <InputEl
          value={norm.en}
          onChange={(e) => handleChange('en', e.target.value)}
          placeholder={placeholder || `${label} (English)`}
          className="flex-1 text-sm"
          required={required}
          readOnly={readOnly}
          data-testid="ml-input-en"
        />
      </div>

      {/* EN validation warning */}
      {required && !completeness.hasEn && (
        <div className="flex items-center gap-1 text-xs text-red-500 pl-10" data-testid="ml-en-required-warning">
          <AlertCircle className="w-3 h-3" />
          English value is required
        </div>
      )}

      {/* Translation toggle bar */}
      <div className="flex items-center gap-2 pl-0">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          data-testid="ml-toggle-languages"
        >
          <Globe className="w-3.5 h-3.5" />
          <span>Translations</span>
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>

        {/* Completeness badge */}
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${completenessColor}`} data-testid="ml-completeness-badge">
          {completeness.filled === completeness.total ? (
            <span className="inline-flex items-center gap-0.5"><Check className="w-3 h-3" /> {completeness.filled}/{completeness.total}</span>
          ) : (
            `${completeness.filled}/${completeness.total} translated`
          )}
        </span>

        {/* Copy from English button */}
        {completeness.hasEn && completeness.filled < completeness.total && !readOnly && (
          <button
            type="button"
            onClick={handleCopyFromEnglish}
            className="flex items-center gap-1 text-[10px] text-blue-500 hover:text-blue-700 transition-colors border border-blue-200 rounded px-1.5 py-0.5 hover:bg-blue-50"
            title="Copy English value to all empty language fields"
            data-testid="ml-copy-from-english"
          >
            <Copy className="w-3 h-3" />
            Copy EN to empty
          </button>
        )}
      </div>

      {/* Other languages — collapsible */}
      {expanded && (
        <div className="space-y-2 border-l-2 border-gray-100 ml-1">
          {OTHER_LANGS.map(lang => {
            const isFilled = !!norm[lang.code]?.trim();
            return (
              <div key={lang.code} className="flex items-start gap-2 pl-2">
                <span className={`inline-flex items-center justify-center h-8 px-2 text-xs font-bold rounded shrink-0 mt-0.5 ${
                  isFilled ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-400'
                }`}>
                  {lang.flag}
                </span>
                <InputEl
                  value={norm[lang.code]}
                  onChange={(e) => handleChange(lang.code, e.target.value)}
                  placeholder={isFilled ? '' : `${label} (${lang.name}) — empty, will show English`}
                  className={`flex-1 text-sm ${!isFilled ? 'border-dashed border-gray-200' : ''}`}
                  dir={lang.code === 'ar' ? 'rtl' : 'ltr'}
                  readOnly={readOnly}
                  data-testid={`ml-input-${lang.code}`}
                />
              </div>
            );
          })}
          <p className="text-[10px] text-gray-400 pl-2 italic">
            Empty fields will display English to users. Use "Copy EN to empty" for quick fill.
          </p>
        </div>
      )}
    </div>
  );
};
